import React from 'react';
import { observer } from 'mobx-react';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import { v4 as uuidv4 } from 'uuid';

import { useStore } from 'utils/hooks/store';

import { DROP_ZONE_EL_ID } from 'core/constants';

import { fileTooLargeMessage, formatUploadLimit, isOverUploadLimit } from 'utils/uploadLimits';

import useFileUpload from 'utils/hooks/useFileUpload';

import { UploadContainer, UploadButton } from '../styles';
import { SUPPORTED_FILE_TYPES, SUPPORTED_FILE_EXTENSIONS } from '../types';

const IOS_GENERIC_NAMES = ['image.jpg', 'image.jpeg', 'photo.jpg', 'photo.jpeg'];

const generateUniqueFileName = (file: File): File => {
  if (!IOS_GENERIC_NAMES.includes(file.name.toLowerCase())) {
    return file;
  }

  const timestamp = Date.now();
  const uniqueId = uuidv4();
  const extension = file.name.substring(file.name.lastIndexOf('.'));
  const newName = `${timestamp}_${uniqueId}${extension}`;

  return new File([file], newName, { type: file.type });
};

const UploadAttachment: React.FunctionComponent<{
  uploadFile?: (file: File) => Promise<void>;
  uploadFiles?: (files: File[]) => Promise<void>;
  multiple?: boolean;
}> = ({ uploadFile, uploadFiles, multiple = false }): React.ReactElement => {
  const { notification } = useStore();

  const getFileExtension = (filename: string): string => filename.toLowerCase().slice(filename.lastIndexOf('.'));

  const validateAndUploadFiles = (files: File[]): void => {
    const validFiles: File[] = [];

    const renamedFiles = files.map(generateUniqueFileName);

    for (const file of renamedFiles) {
      const isValidType =
        SUPPORTED_FILE_TYPES.includes(file.type) || SUPPORTED_FILE_EXTENSIONS.includes(getFileExtension(file.name));
      if (isValidType) {
        if (isOverUploadLimit(file)) {
          notification.enqueueErrorSnackbar(fileTooLargeMessage(file));
        } else {
          validFiles.push(file);
        }
      } else {
        notification.enqueueErrorSnackbar(
          `File "${file.name}" has unsupported type. ` +
            `Allowed formats: PDF, DOCX, XLSX, PPTX, JPG, PNG, TXT, MP4, MOV, CSV, EML, MSG.`
        );
      }
    }

    if (validFiles.length > 0) {
      // Use uploadFiles if available (supports batch), otherwise fall back to uploadFile (one at a time)
      if (uploadFiles) {
        uploadFiles(validFiles);
      } else if (uploadFile) {
        validFiles.forEach((file) => uploadFile(file));
      }
    }
  };

  const handleDroppedFiles = (file: File): void => {
    validateAndUploadFiles([file]);
  };

  const handleDroppedMultipleFiles = (files: File[]): void => {
    validateAndUploadFiles(files);
  };

  const { dragActive, fileRef, dropZoneRef } = useFileUpload(
    handleDroppedFiles,
    multiple ? handleDroppedMultipleFiles : undefined
  );

  const onFileUpload = async ({ target }: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (target.files) {
      validateAndUploadFiles(Array.from(target.files));
    }
    // Reset input value to allow selecting the same file again
    target.value = '';
  };

  return (
    <>
      <input hidden onChange={onFileUpload} type="file" ref={fileRef} multiple={multiple} />
      <UploadContainer ref={dropZoneRef} data-testid={DROP_ZONE_EL_ID} $dragActive={dragActive}>
        <AttachFileIcon sx={{ fontSize: 24 }} color="primary" />
        <h1 style={{ margin: 0 }}>
          Drop files here or{' '}
          <UploadButton type="button" onClick={() => fileRef.current?.click()}>
            click to upload
          </UploadButton>{' '}
          (up to {formatUploadLimit()})
        </h1>
      </UploadContainer>
    </>
  );
};

export default observer(UploadAttachment);
